import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  formControlChanges$,
  TypedAbstractControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { MohAgreementHolder } from '@principle-theorem/principle-core';
import {
  IPractice,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import { filterUndefined, WithRef } from '@principle-theorem/shared';
import { switchMap, take, tap } from 'rxjs/operators';
import {
  IMohAgreementHolderForm,
  MohAgreementHolderProvider,
} from '../../../moh/moh-agreement-holder-form';

@Component({
  selector: 'pr-moh-agreement-holder-form',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
  ],
  templateUrl: './moh-agreement-holder-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MohAgreementHolderFormComponent {
  form = input.required<TypedFormGroup<IMohAgreementHolderForm>>();
  practice = input.required<WithRef<IPractice>>();
  practitioner = input.required<WithRef<IStaffer>>();

  constructor() {
    toObservable(this.form)
      .pipe(
        take(1),
        tap((form) => form.controls.provider.setValue(this._defaultProvider())),
        switchMap((form) => formControlChanges$(form.controls.provider)),
        filterUndefined(),
        takeUntilDestroyed()
      )
      .subscribe((provider) => this._providerSelected(provider));
  }

  private _providerSelected(provider: MohAgreementHolderProvider): void {
    const { payeeNumber, agreementNumber } = MohAgreementHolder.fromProvider(
      provider,
      this.practice()
    );
    this.form().patchValue({ payeeNumber, agreementNumber });
    this._lockWhenProvided(this.form().controls.payeeNumber, payeeNumber);
    this._lockWhenProvided(
      this.form().controls.agreementNumber,
      agreementNumber
    );
  }

  private _lockWhenProvided(
    control: TypedAbstractControl<string>,
    value: string
  ): void {
    if (value) {
      control.disable();
      return;
    }
    control.enable();
  }

  private _defaultProvider(): MohAgreementHolderProvider {
    return MohAgreementHolder.claimsUnderPractice(this.practice())
      ? this.practice()
      : this.practitioner();
  }
}
